import { useState, useMemo, useEffect, useRef, KeyboardEvent } from "react";
import { ArrowDownRight, ArrowUpRight, RefreshCw, ShieldAlert, Activity, CalendarDays } from "lucide-react";
import type { TimelineEntry } from "../../api/status";
import "../../styles/dashboard-hero.css";

interface OverviewHeroProps {
  userName?: string;
  timelineEntries?: TimelineEntry[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

export default function OverviewHero({
  userName = "Digvijay",
  timelineEntries = [],
  loading = false,
  error = null,
  onRetry,
}: OverviewHeroProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [displayCount, setDisplayCount] = useState<number>(0);
  const isFirstLoadRef = useRef<boolean>(true);

  // Format Name: Display name if present, otherwise format username ("digvijay.k" -> "Digvijay")
  const formattedName = useMemo(() => {
    if (!userName) return "Digvijay";
    const clean = userName.split("@")[0].trim();
    if (!clean) return "Digvijay";
    const parts = clean.split(/[._-]/);
    const firstName = parts[0] || clean;
    return firstName.charAt(0).toUpperCase() + firstName.slice(1);
  }, [userName]);

  // Current formatted date
  const todayFormatted = useMemo(() => {
    return new Date().toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  // Process 14 days of data ending today
  const chartBars = useMemo(() => {
    const countMap = new Map<string, number>();
    for (const entry of timelineEntries) {
      if (entry?.date) {
        countMap.set(entry.date, typeof entry.count === "number" ? entry.count : 0);
      }
    }

    const bars: {
      isoDate: string;
      dayOfWeek: string;
      fullDate: string;
      dateLabel: string;
      count: number;
      isToday: boolean;
    }[] = [];
    const now = new Date();

    for (let i = 13; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dayNum = String(d.getDate()).padStart(2, "0");
      const iso = `${y}-${m}-${dayNum}`;

      const dayOfWeek = d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
      const fullDate = d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const monthShort = d.toLocaleDateString("en-US", { month: "short" });
      const dateLabel = `${monthShort} ${dayNum}`;

      const count = countMap.get(iso) ?? 0;
      const isToday = i === 0;

      bars.push({ isoDate: iso, dayOfWeek, fullDate, dateLabel, count, isToday });
    }

    return bars;
  }, [timelineEntries]);

  // Calculate 7-day incident summary & 7-day previous period comparison
  const { incidentsLast7Days, pctChange, isIncrease, dailyAvg } = useMemo(() => {
    if (chartBars.length === 0) {
      return { incidentsLast7Days: 0, pctChange: 0, isIncrease: false, dailyAvg: 0 };
    }

    const current7 = chartBars.slice(7, 14);
    const previous7 = chartBars.slice(0, 7);

    const sumCurrent = current7.reduce((acc, item) => acc + item.count, 0);
    const sumPrev = previous7.reduce((acc, item) => acc + item.count, 0);

    let pct = 0;
    if (sumPrev > 0) {
      pct = Math.round(Math.abs((sumCurrent - sumPrev) / sumPrev) * 100);
    } else if (sumCurrent > 0) {
      pct = 100;
    }

    return {
      incidentsLast7Days: sumCurrent,
      pctChange: pct,
      isIncrease: sumCurrent >= sumPrev,
      dailyAvg: Math.round(sumCurrent / 7),
    };
  }, [chartBars]);

  // Headline Number Count-Up Animation on FIRST load only
  useEffect(() => {
    if (incidentsLast7Days === 0) {
      setDisplayCount(0);
      return;
    }

    if (!isFirstLoadRef.current) {
      setDisplayCount(incidentsLast7Days);
      return;
    }

    let start = 0;
    const duration = 500;
    const startTime = performance.now();

    const animateCount = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(start + easedProgress * (incidentsLast7Days - start));
      setDisplayCount(current);

      if (progress < 1) {
        requestAnimationFrame(animateCount);
      } else {
        setDisplayCount(incidentsLast7Days);
        isFirstLoadRef.current = false;
      }
    };

    requestAnimationFrame(animateCount);
  }, [incidentsLast7Days]);

  // Maximum value for scaling bar heights
  const maxVal = useMemo(
    () => Math.max(...chartBars.map((b) => b.count), 1),
    [chartBars]
  );

  // Keyboard navigation across 14 slots
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (activeIndex === null) {
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === "ArrowRight") {
      e.preventDefault();
      setActiveIndex((prev) => (prev !== null ? Math.min(prev + 1, 13) : 0));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setActiveIndex((prev) => (prev !== null ? Math.max(prev - 1, 0) : 0));
    } else if (e.key === "Escape") {
      e.preventDefault();
      setActiveIndex(null);
    }
  };


  const dateRangeLabel = useMemo(() => {
    if (chartBars.length === 0) return "";
    return `${chartBars[0].dateLabel} – ${chartBars[chartBars.length - 1].dateLabel}`;
  }, [chartBars]);

  return (
    <div className="tx-hero">
      {/* ── TOP GREETING HEADER STRIP ── */}
      <div className="tx-hero-header-strip">
        <div className="tx-hero-greeting-box">
          <h1 className="tx-hero-greeting-title">
            Hi <span className="tx-hero-name-gradient">{formattedName}</span>,
          </h1>
          <p className="tx-hero-greeting-subtitle">
            Here's your real-time threat intelligence overview for the day
          </p>
        </div>

        <div className="tx-hero-header-meta">
          <div className="tx-hero-date-pill">
            <CalendarDays size={14} className="text-indigo-400" />
            <span>{todayFormatted}</span>
          </div>
        </div>
      </div>

      {/* ── UNIFIED HERO OVERVIEW CARD ── */}
      <div className="tx-hero-main-card">
        <div className="tx-hero-grid">
          {/* HERO LEFT METRIC BLOCK */}
          <div className="tx-hero-left">
            <div className="tx-hero-metric-header">
              <div className="tx-hero-metric-icon">
                <ShieldAlert size={18} />
              </div>
              <span className="tx-hero-metric-label">Incidents in last 7 days</span>
            </div>

            {/* BOLD HEADLINE NUMBER IN OUTFIT FONT */}
            <div className="tx-hero-metric-value-wrap">
              <div className="tx-hero-metric-value">
                {displayCount.toLocaleString("en-US")}
              </div>
            </div>

            <div className="tx-hero-delta-line">
              <span
                className={`tx-hero-delta-badge ${isIncrease ? "delta-up" : "delta-down"}`}
              >
                {isIncrease ? <ArrowUpRight size={15} /> : <ArrowDownRight size={15} />}
                <span>{pctChange}%</span>
              </span>
              <span className="tx-hero-delta-text">
                {isIncrease ? "up from previous period" : "down from previous period"}
              </span>
            </div>

            {/* Micro Stats Row */}
            <div className="tx-hero-micro-row">
              <div className="tx-hero-micro-stat">
                <span className="tx-hero-micro-label">Daily Average</span>
                <span className="tx-hero-micro-val">~{dailyAvg.toLocaleString()} / day</span>
              </div>
              <div className="tx-hero-micro-stat">
                <span className="tx-hero-micro-label">Weekly Trend</span>
                <span className={`tx-hero-micro-val ${isIncrease ? "text-amber-400" : "text-emerald-400"}`}>
                  {isIncrease ? "Increasing" : "Decreasing"}
                </span>
              </div>
            </div>
          </div>

          {/* VERTICAL DIVIDER */}
          <div className="tx-hero-divider" />

          {/* HERO RIGHT 14-BAR CHART */}
          <div
            className="tx-hero-chart-container"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onMouseLeave={() => setActiveIndex(null)}
            aria-label="14-day incident timeline bar chart"
          >
            {/* Chart Sub-Header */}
            <div className="tx-hero-chart-subhead">
              <div className="tx-hero-chart-title">
                <Activity size={14} className="text-indigo-400" />
                <span>14-Day Timeline</span>
                {dateRangeLabel && (
                  <span className="tx-hero-chart-range">({dateRangeLabel})</span>
                )}
              </div>
              <div className="tx-hero-chart-legend">
                <span className="tx-hero-legend-dot" />
                <span>Daily Incidents</span>
              </div>
            </div>

            {/* SKELETON / LOADING STATE */}
            {loading && (
              <div className="tx-hero-plot-area" style={{ opacity: 0.5 }}>
                <div className="tx-hero-slots-row">
                  {Array.from({ length: 14 }).map((_, i) => (
                    <div key={i} className="tx-hero-slot">
                      <div
                        className="tx-hero-bar"
                        style={{
                          height: `${30 + (i % 5) * 15}%`,
                          opacity: 0.4,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ERROR STATE */}
            {error && !loading && (
              <div
                style={{
                  height: 220,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  background: "rgba(239, 68, 68, 0.05)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                  borderRadius: 12,
                  color: "#ef4444",
                  padding: 24,
                }}
              >
                <span style={{ fontSize: "0.88rem", fontWeight: 600 }}>{error}</span>
                {onRetry && (
                  <button
                    onClick={onRetry}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 14px",
                      borderRadius: 6,
                      background: "#ef4444",
                      color: "#ffffff",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                    }}
                  >
                    <RefreshCw size={13} /> Retry
                  </button>
                )}
              </div>
            )}

            {/* NORMAL CHART DISPLAY */}
            {!loading && !error && (
              <div className="tx-hero-plot-area">
                {/* 14 SLOTS FLEX ROW */}
                <div className="tx-hero-slots-row">
                  {chartBars.map((bar, idx) => {
                    const barHeightPx = bar.count > 0 ? Math.max(Math.round((bar.count / maxVal) * 115), 6) : 6;
                    const isHovered = activeIndex === idx;

                    return (
                      <div
                        key={bar.isoDate}
                        className="tx-hero-slot"
                        onMouseEnter={() => setActiveIndex(idx)}
                        onMouseLeave={() => setActiveIndex(null)}
                        onClick={() => setActiveIndex(idx)}
                        aria-label={`${bar.fullDate}: ${bar.count} incidents`}
                        role="button"
                        tabIndex={0}
                      >
                        <div
                          className={`tx-hero-bar ${isHovered ? "bar-active" : ""} ${isFirstLoadRef.current ? "tx-hero-bar-animate" : ""}`}
                          style={{
                            height: `${barHeightPx}px`,
                            animationDelay: `${idx * 20}ms`,
                          }}
                        >
                          {isHovered && <div className="tx-hero-bar-glow" />}

                          {/* EACH BAR SHOWS ITS OWN CARD DIRECTLY ABOVE IT */}
                          {isHovered && (
                            <div
                              className={`tx-hero-bar-tooltip ${
                                idx === 0
                                  ? "tooltip-edge-left"
                                  : idx === chartBars.length - 1
                                  ? "tooltip-edge-right"
                                  : "tooltip-center"
                              }`}
                            >
                              <div className="tx-hero-card-weekday">{bar.dayOfWeek}</div>
                              <div className="tx-hero-card-date">{bar.fullDate}</div>
                              <div className="tx-hero-card-count-wrap">
                                <span className="tx-hero-card-count">
                                  {bar.count.toLocaleString("en-US")}
                                </span>
                                <span className="tx-hero-card-unit">incidents</span>
                              </div>
                              {bar.isToday && (
                                <div className="tx-hero-card-today">Today, so far</div>
                              )}
                              <div className="tx-hero-card-caret" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* DATE LABELS ROW */}
                <div className="tx-hero-labels-row">
                  {chartBars.map((bar) => (
                    <div key={bar.isoDate} className="tx-hero-date-label">
                      {bar.dateLabel}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ACCESSIBILITY VISUALLY HIDDEN TABLE */}
            <div className="sr-only">
              <table>
                <caption>14-Day Incident Timeline Summary</caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Incidents</th>
                  </tr>
                </thead>
                <tbody>
                  {chartBars.map((bar) => (
                    <tr key={bar.isoDate}>
                      <td>{bar.fullDate}</td>
                      <td>{bar.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
